/**
 * Cadence agent — governed check-in proposals for accounts that have gone
 * quiet past their touch cadence (Ask #1).
 *
 * The pure engine (`crm/cadence.ts`) decides *which* accounts are overdue and
 * what the next touch should be; this thin producer turns each verdict into a
 * real `create_task` proposal in the approvals inbox — the same governed path
 * `customerHealthAgent` uses. It performs no write itself: the operator
 * approves in the Dashboard inbox, `resolveApproval` executes through
 * `runActionWithUndo`, and every step lands in the Decision Ledger.
 *
 *   real touch history  →  real proposal  →  operator approval  →  real write
 *
 * De-dupes by the deterministic proposal title against pending approvals so
 * re-running never piles duplicate check-ins for the same account.
 */

import { dueTouches, cadenceActionFor, type CadenceOverrides } from '../crm/cadence';
import { queueApproval, listApprovals } from './approvals';
import type { Account, Person } from '../db/types';
import type { CreateTaskAction } from './types';

export interface ProposeCadenceCheckinsResult {
  /** How many new check-in proposals were queued for approval. */
  proposed: number;
  /** Overdue accounts that already had a matching pending proposal. */
  skipped: number;
}

/**
 * Compute overdue touches and queue one governed `create_task` proposal per
 * overdue account. Returns how many were proposed vs. skipped (deduped).
 */
export async function proposeCadenceCheckins(
  accounts: Account[],
  people: Person[],
  now = Date.now(),
  overrides: CadenceOverrides = {},
): Promise<ProposeCadenceCheckinsResult> {
  const due = dueTouches(accounts, people, now, overrides);
  if (due.length === 0) return { proposed: 0, skipped: 0 };

  // Don't propose a second touch for an account that already has one waiting.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'create_task')
      .map((a) => (a.action as CreateTaskAction).title),
  );

  let proposed = 0;
  let skipped = 0;
  for (const d of due) {
    const action = cadenceActionFor(d);
    if (alreadyQueued.has(action.title)) {
      skipped++;
      continue;
    }
    // The note carries the engine's own reasoning so the inbox explains itself.
    await queueApproval(action, 'scheduler', d.reason);
    alreadyQueued.add(action.title);
    proposed++;
  }

  return { proposed, skipped };
}
